/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "config/firebaseWithFirestore";
import { logoutSuccess, logoutError } from "redux/actions/Login";
import { allowPaths } from "redux/actions/Security";
import AppContext from "AppContext";
import { stableSort } from "components/common_components/Tables/sortComparators";
import Avatar from "../common_components/Avatar";
import Button from "../common_components/Button";
import Icon from "../common_components/Icon";
import { getEnvVar } from "utils/env";
import MenuItem from "./MenuItem";
import { Tenant } from "utils/tenants";
import Version from "components/common_components/Version";
// import FloatingNotifications from "components/Home/Notifications/FloatingNotifications";

const initialMenuSections = (user) => [
    {
        index: 1,
        options: [
            {
                index: 4,
                key: "home",
                label: "Home",
                path: "/",
                isHome: true,
                icon: "home",
                notificationsKeyIdentity: user.canChatWithClients ? getEnvVar("REACT_APP_CHAT_IDENTITY") : undefined,
            },
            { index: 5, key: "calendar", label: "Calendar", path: "/calendar", icon: "calendar" },
            {
                index: 6,
                key: "chat",
                label: "Chat",
                path: "/chat",
                icon: "chat",
                notificationsKeyIdentity: String(user.email).replace("@", "|_at_|"),
            },
            user.canUsePos && {
                index: 7,
                key: "pos",
                label: "POS",
                path: "/pos",
                icon: "pos",
            },
            (user.canUseInvoicing || false) && {
                index: 8,
                key: "invoicing",
                label: "Invoicing",
                path: "/invoicing",
                icon: "list",
            },
            Tenant.getFeatures().forms.enabled && {
                index: 9,
                key: "forms",
                label: "Forms",
                path: "/forms",
                icon: "form",
                betaFeature: true,
            },
        ],
    },
    {
        index: 2,
        options: [
            { index: 7, key: "customers", label: "Clients", path: "/customers", icon: "customers" },
            Tenant.getFeatures().visitors.enabled && { index: 8, key: "visitors", label: "Visitors", path: "/visitors", icon: "visitors" },
        ],
    },
    {
        index: 3,
        options: [{ index: 9, key: "settings", label: "Settings", path: "/settings", icon: "settings" }],
    },
];

function Menu({ children, history }) {
    const [navOpen, setNavigation] = useState(true);

    const [selectedOption, setSelectedOption] = useState();
    const [modules, setModules] = useState([]);
    const [menuSections, setMenuSections] = useState([]);

    const { user } = useSelector((state) => state.Login);
    const dispatch = useDispatch();

    const toggleNavigation = () => {
        setNavigation(!navOpen);
    };

    const {
        api: {
            Settings: { Modules },
        },
        setModule,
        setAllowedModules,
    } = useContext(AppContext);

    function getModules() {
        Modules.getAll({
            onSuccess: setModules,
            onError: () => {
                setModules([]);
            },
        });
    }

    useEffect(() => {
        getModules();
    }, []);

    useEffect(() => {
        const initMenuSections = initialMenuSections(user);
        let finalMenuSections = [initMenuSections[0], initMenuSections[1]];
        let moduleOptions;

        const setUpSections = () => {
            if (user.isAdmin) {
                finalMenuSections.push(initMenuSections[2]);
            }

            setMenuSections(finalMenuSections);
            setSelectedOption(finalMenuSections[0].options.filter((o) => o.path));
        };

        if (modules && modules.length > 0) {
            const { modules: userModules } = user;
            if (userModules && userModules.length > 0) {
                setAllowedModules(userModules);
                moduleOptions = stableSort(userModules, "asc", "name").map((module, index) => {
                    return {
                        ...module,
                        index,
                        key: module.code,
                        label: module.name,
                        path: module.path,
                        icon: module.code,
                    };
                });
                finalMenuSections.push({ index: 0, options: moduleOptions });
                setUpSections();
            } else {
                setUpSections();
            }
        }
    }, [modules, user]);

    useEffect(() => {
        if (menuSections && menuSections.length > 0) {
            const flattedOptions = menuSections.reduce((newOptions, { options }) => {
                return [...newOptions, ...options];
            }, []);
            const newSelectedOption = flattedOptions.find((o) => {
                if (o.isHome) {
                    return o.path && history.location.pathname === "/";
                }
                return o.path && history.location.pathname.startsWith(o.path);
            });
            if (newSelectedOption) {
                setModule(modules.some((x) => x.code === newSelectedOption.key) ? newSelectedOption : undefined);
                setSelectedOption(newSelectedOption);
            }
            const allowedPaths = flattedOptions.map(({ path }) => path);
            dispatch(allowPaths(allowedPaths));
        }
    }, [history.location.pathname, menuSections]);

    function doLogout() {
        logout()
            .then(() => {
                dispatch(logoutSuccess());
                localStorage.clear();
            })
            .catch((error) => dispatch(logoutError(error.message)));
    }

    return (
        <div className="wrapper">
            <nav className={`nav ${navOpen ? "" : "nav--small"}`}>
                <Version short />
                <div className="nav__logo">
                    <img className="menu__logo" src={Tenant.getLogo()} alt="menu-logo-img" />
                </div>
                <div className="nav__header nav-inner">
                    <p className="text--white hide-nav-small">{Tenant.getName()}</p>

                    <div className="nav-toggle" onClick={toggleNavigation}>
                        <Icon name="arrow" color="primary" size="sm" />
                        <span className="font-small text--grey hide-nav-small">Collapse</span>
                    </div>
                </div>

                <div className="nav__body">
                    {menuSections
                        .filter((section) => section.options.length > 0)
                        .map(({ index, options }) => {
                            return (
                                <div className="nav-list" key={index}>
                                    {options
                                        .filter((x) => Boolean(x))
                                        .map((option) => {
                                            const { key, label, path, icon, notificationsKeyIdentity, betaFeature } = option;
                                            const selected = selectedOption ? selectedOption.key === key : false;
                                            return (
                                                <MenuItem
                                                    key={key}
                                                    id={key}
                                                    selected={selected}
                                                    label={label}
                                                    path={path}
                                                    icon={icon}
                                                    notificationsKeyIdentity={notificationsKeyIdentity}
                                                    betaFeature={betaFeature}
                                                    onClick={() => {
                                                        setModule(modules.some((x) => x.code === key) ? option : undefined);
                                                    }}
                                                />
                                            );
                                        })}
                                </div>
                            );
                        })}
                </div>

                <div className="nav__footer nav-inner">
                    <div className="user">
                        <Avatar name={user.name} />
                        <div className="user__name hide-nav-small font-small">
                            <div className="name text--white">{user.name}</div>
                        </div>
                    </div>
                    <Button link key="logout" onClick={doLogout}>
                        Logout
                    </Button>
                </div>
            </nav>
            <main className={`main ${navOpen ? "" : "main--small"}`}>
                <div className={`main-actions ${navOpen ? "" : "main-actions--small"}`}>{/* <FloatingNotifications /> */}</div>
                {children}
            </main>
        </div>
    );
}

export default Menu;
